import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"
import { navigator } from "@hotwired/turbo"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import { Style, Stroke, Fill } from "ol/style"
import GeoJSON from "ol/format/GeoJSON"
import i18next, { i18nReady } from "../utils/i18next"
import map, { japanSimpleMapLayer, japanDetailedMapLayer, japanEEWMapLayer, japanPrefMapLayer } from "../utils/map"
import { getAssetUrl } from "../utils/assets"
import {
  INTENSITY_ORDER_ASC,
  TSUNAMI_LEVEL_ORDER,
  getIntensityStyle,
  getEpicenterStyle,
  buildShindoFeaturesFromDict,
  createVectorLayerFromFeatures,
  fitToLayer,
  reportInfoWrite,
  resetMapForReport,
  startTsunamiBlink,
  showBaseLayer,
  applyReportLayers,
} from "../utils/report_layers"
import {
  setFullscreenTip,
  showFullscreenTip,
  setTitle,
  setTsunamiLegend,
  showTsunamiLegend,
  hideTitle,
  showEEWLegend,
  setEEWLegend,
  showInfoLegend,
  setInfoLegend,
} from "../utils/map_ui"
import { startWaveAnimation, ensureTimeLabel, cleanupWaves } from "../utils/wave_animation"
// 副作用导入:注册震度图标的悬浮提示监听
import { hideFeatureTooltip } from "../utils/map_tooltip"

export default class extends Controller {
  static targets = ["report", "newReport", "listBottom", "list"]
  static values = {
    activeId: String,
    page: { type: Number, default: 1 },
    pages: Number,
    pageName: { type: String, default: "event" },
  }

  // 渲染会话号:快速切换报告时废弃仍在途的异步渲染,避免旧图层晚到
  renderSession = 0

  connect() {
    if (this.activeIdValue) {
      const el = document.getElementById(this.activeIdValue)
      el && el.click()
    } else if (this.hasReportTarget) {
      this.reportTarget.click()
    }
  }

  disconnect() {
    cleanupWaves()
  }

  // 进度条
  showProgressBar() {
    navigator.delegate.adapter.progressBar.setValue(0)
    navigator.delegate.adapter.progressBar.show()
  }

  hideProgressBar() {
    navigator.delegate.adapter.progressBar.setValue(1)
    navigator.delegate.adapter.progressBar.hide()
  }

  withProgress(request) {
    this.showProgressBar()
    return request.then((response) => {
      this.hideProgressBar()
      return response
    })
  }

  get(url, options) {
    const request = new FetchRequest("get", url, options)
    return this.withProgress(request.perform())
  }

  viewUrl(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    window.open(url, "_blank")?.focus()
  }

  newReportTargetConnected(target) {
    const latestSwitch = localStorage.getItem("latestSwitch")
    if (latestSwitch !== "false") target.click()
  }

  // 统一获取“同类最新信息”
  async getLatestInfoFromReportLists(type, currentReportJSON = this.currentEntry) {
    // currentReportJSON 应为包含 { id, report: {...} } 的对象
    const currentReportID = currentReportJSON?.report?.id ?? currentReportJSON?.id

    if (this.pageNameValue === "event") {
      const response = await this.get(`/api/v1/events/${this.activeIdValue}/search_intensity`, {
        query: { name: type, report_id: currentReportID },
      })
      const body = await response.json
      return body
    }

    // 非 event 页从当前 DOM 列表里找
    let currentReportsJSON
    if (this.hasNewReportTarget) {
      currentReportsJSON = this.newReportTargets.concat(this.reportTargets).map((html) => JSON.parse(html.dataset.json))
    } else {
      currentReportsJSON = this.reportTargets.map((html) => JSON.parse(html.dataset.json))
    }
    let report = null
    currentReportsJSON.forEach((json) => {
      const rid = json?.report?.id
      const rname = json?.report?.name
      if (rid != null && currentReportID != null && rid < currentReportID && rname === type && report === null) {
        report = json
      }
    })
    return report
  }

  // —— 入口 ——
  async updateMap(event) {
    event.preventDefault()

    if (this.activeIdValue) {
      const active = document.getElementById(this.activeIdValue)
      active && active.classList.remove("active")
    }
    event.currentTarget.classList.add("active")

    this.activeIdValue = event.currentTarget.id
    this.currentEntry = JSON.parse(event.currentTarget.dataset.json) // 保存完整 JSON
    const report = this.currentEntry.report

    const run = () => this.updateShinyMap(report)
    if (window.mapLoaded) {
      run()
    } else {
      map.once("rendercomplete", () => {
        run()
        window.mapLoaded = true
      })
    }
  }

  // —— 主调度 ——
  async updateShinyMap(report) {
    const session = ++this.renderSession
    await i18nReady
    if (session !== this.renderSession) return

    resetMapForReport()
    hideFeatureTooltip()

    const reportInfo = document.getElementById("reportInfo")
    reportInfo.innerHTML = `${report.name}`
    if (report.url) reportInfo.innerHTML += ` <a href="${report.url}" target="_blank">View</a>`
    if (report.head?.headline_text) reportInfo.innerHTML += `<br/>${report.head.headline_text}`

    // 按类型分派
    if (report.name === "震度速報") return this.renderIntensityEarlyReport(report, reportInfo, session)
    if (report.name === "震源に関する情報") return this.renderEpicenterReport(report, reportInfo, session)
    if (report.name === "震源・震度に関する情報" || report.name === "顕著な地震の震源要素更新のお知らせ")
      return this.renderQuakeAndIntensityReport(report, reportInfo, session)
    if (report.name === "津波警報・注意報・予報a") return this.renderTsunamiReport(report, reportInfo, session)
    if (report.name?.includes("緊急地震速報")) return this.renderEEWReport(report, reportInfo, session)

    // 其他通用:收起报告图层与底图,只显示世界地图
    applyReportLayers([])
    showBaseLayer(null)
    if (report.head?.info_type === "取消") {
      if (report.text?.text) {
        setFullscreenTip(report.text.text)
        showFullscreenTip()
      }
      return
    }
    setTitle(report.name)
    reportInfo.innerHTML = `【${report.name}】<br/>`
    setFullscreenTip(i18next.t("map.tips.see_info"))
    showFullscreenTip()

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // 报告被取消时统一只展示全文提示
  renderCancelFallback(report) {
    applyReportLayers([])
    showBaseLayer(null)
    if (report.text?.text) {
      setFullscreenTip(report.text.text)
      showFullscreenTip()
    }
  }

  // —— 各类型渲染 ——

  // 震度速報:各地震度 + 同事件最新震源
  async renderIntensityEarlyReport(report, reportInfo, session) {
    setTitle(i18next.t("xml.report.name.intensity_early_report"))
    if (report.head?.info_type === "取消") return this.renderCancelFallback(report)

    const { default: areaData } = await import("../map/jma_area.json")
    if (session !== this.renderSession) return

    const shindo = report.earth_quake?.intensity?.observation?.data?.area || {}
    showBaseLayer(japanSimpleMapLayer)

    // 信息面板
    reportInfoWrite(shindo, reportInfo)

    // 要素
    const features = buildShindoFeaturesFromDict(shindo, areaData)

    // 震中(取同一事件中的“震源に関する情報”)
    const epiWrap = await this.getLatestInfoFromReportLists("震源に関する情報", this.currentEntry)
    if (session !== this.renderSession) return
    const epicenterReport = epiWrap?.report
    const styleMap = {}
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = getIntensityStyle(getAssetUrl, k)

    if (epicenterReport?.earth_quake?.epicenters?.[0]) {
      const e = epicenterReport.earth_quake.epicenters[0]
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [e.longitude, e.latitude] },
        properties: { intensity: "x" },
      })
      styleMap["x"] = getEpicenterStyle(getAssetUrl, e.name, 2)
    }

    const shindoLayer = createVectorLayerFromFeatures(features, styleMap, "shindo")
    applyReportLayers([shindoLayer])
    fitToLayer(shindoLayer, 7.9)

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // 震源に関する情報:震中位置,叠加同事件已有震度
  async renderEpicenterReport(report, reportInfo, session) {
    setTitle(i18next.t("xml.report.name.epicenter_report"))
    if (report.head?.info_type === "取消") return this.renderCancelFallback(report)

    const e = report.earth_quake?.epicenters?.[0]
    if (!e) return

    // 若存在同一事件的震度信息,叠加显示
    const intensityInfo = await this.getLatestInfoFromReportLists("震度速報", this.currentEntry)
    if (session !== this.renderSession) return
    const shindo = intensityInfo?.report?.earth_quake?.intensity?.observation?.data?.area || {}

    const { default: areaData } = await import("../map/jma_area.json")
    if (session !== this.renderSession) return

    const features = [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: [e.longitude, e.latitude] },
        properties: { intensity: "x" },
      },
    ]
    features.push(...buildShindoFeaturesFromDict(shindo, areaData))

    showBaseLayer(japanSimpleMapLayer)

    const styleMap = { x: getEpicenterStyle(getAssetUrl, e.name, 2) }
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = getIntensityStyle(getAssetUrl, k)

    const shindoLayer = createVectorLayerFromFeatures(features, styleMap, "shindo")
    applyReportLayers([shindoLayer])
    fitToLayer(shindoLayer, 7.9)

    if (shindo && Object.keys(shindo).length > 0) reportInfoWrite(shindo, reportInfo)
    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // 震源・震度に関する情報(含顕著な地震の更新):按城市绘制震度
  async renderQuakeAndIntensityReport(report, reportInfo, session) {
    if (report.head?.title === "遠地地震に関する情報") {
      setTitle(i18next.t("xml.report.name.overseas_earthquake"))
    } else if (report.name === "顕著な地震の震源要素更新のお知らせ") {
      setTitle(i18next.t("xml.report.name.earthquake_updated"))
    } else {
      setTitle(i18next.t("xml.report.name.earth_quake_intensity_report"))
    }

    if (report.head?.info_type === "取消") return this.renderCancelFallback(report)

    const epicenters = report.earth_quake?.epicenters || []
    const e = epicenters[epicenters.length - 1]
    if (!e) return

    // 强震要素更新时,从同类最新“震源・震度に関する情報”取震度
    let intensityInfo = report.earth_quake?.intensity
    if (report.name === "顕著な地震の震源要素更新のお知らせ") {
      const wrap = await this.getLatestInfoFromReportLists("震源・震度に関する情報", this.currentEntry)
      if (session !== this.renderSession) return
      intensityInfo = wrap?.report?.earth_quake?.intensity
    }

    const { default: cityData } = await import("../map/jma_city.json")
    if (session !== this.renderSession) return

    const shindo = intensityInfo?.observation?.data?.city || {}
    const features = [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: [e.longitude, e.latitude] },
        properties: { intensity: "x" },
      },
    ]
    features.push(...buildShindoFeaturesFromDict(shindo, cityData))

    showBaseLayer(japanDetailedMapLayer)

    const styleMap = { x: getEpicenterStyle(getAssetUrl, report.earth_quake?.epicenters?.[0]?.name || "", 1) }
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = getIntensityStyle(getAssetUrl, k)

    const shindoLayer = createVectorLayerFromFeatures(features, styleMap, "shindo")
    applyReportLayers([shindoLayer])
    fitToLayer(shindoLayer, 7.9)

    if (shindo && Object.keys(shindo).length > 0) reportInfoWrite(shindo, reportInfo)
    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // 津波警報・注意報・予報a
  async renderTsunamiReport(report, reportInfo, session) {
    setTitle(i18next.t("xml.report.name.tsunami_report"))
    if (report.head?.info_type === "取消") return this.renderCancelFallback(report)

    const type = report.tsunami?.tsunami_type
    if (type === "cancel" || type === "forecast") {
      showTsunamiLegend()
      setTsunamiLegend(
        i18next.t("xml.report.tsunami.alert"),
        i18next.t("xml.report.tsunami.warning"),
        i18next.t("xml.report.tsunami.notice"),
      )
      setFullscreenTip(i18next.t("map.tips.no_tsunami"))
      showFullscreenTip()
      showBaseLayer(japanPrefMapLayer)
      applyReportLayers([])
      hideTitle()

      if (type === "forecast") {
        const tsunamiAreas = report.tsunami?.areas || {}
        if (tsunamiAreas["forecast"]) {
          reportInfo.innerHTML += `<br/>[若干の海面変動]`
          for (const area of tsunamiAreas["forecast"]) {
            if (area) reportInfo.innerHTML += ` ${area}`
          }
        }
      }
      if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
      return
    }

    // 警报/注意报绘制
    const { default: tsunamiAreaData } = await import("../map/jma_tsunami_area.json")
    if (session !== this.renderSession) return

    const tsunamiAreas = report.tsunami?.areas || {}

    const areaLevelMap = {}
    for (const key of TSUNAMI_LEVEL_ORDER) {
      const list = tsunamiAreas[key]
      if (list && list.length > 0) {
        for (const name of list) areaLevelMap[name] = key
      }
    }

    reportInfoWrite(tsunamiAreas, reportInfo, "tsunami")

    const areaNames = Object.keys(areaLevelMap)
    tsunamiAreaData.features = tsunamiAreaData.features.filter((f) => areaNames.includes(f.properties.name))

    showTsunamiLegend()
    showBaseLayer(japanPrefMapLayer)

    const coastlineStyles = {
      notice: new Style({ stroke: new Stroke({ color: "#faf500", width: 3 }) }),
      warning: new Style({ stroke: new Stroke({ color: "#ff2800", width: 3 }) }),
      alert: new Style({ stroke: new Stroke({ color: "#c800ff", width: 3 }) }),
    }
    const coastlineStyleFunction = (feature) => coastlineStyles[areaLevelMap[feature.getProperties()["name"]]]

    const tsunamiWarningLayer = new VectorLayer({
      source: new VectorSource({
        features: new GeoJSON().readFeatures(tsunamiAreaData, { featureProjection: "EPSG:3857" }),
      }),
      style: coastlineStyleFunction,
    })
    tsunamiWarningLayer.set("name", "tsunami_warning")
    // 把预警等级写到要素上,供悬浮提示显示
    for (const feature of tsunamiWarningLayer.getSource().getFeatures()) {
      feature.set("level", areaLevelMap[feature.get("name")])
    }
    applyReportLayers([tsunamiWarningLayer])
    fitToLayer(tsunamiWarningLayer, 7)

    // 启动闪烁:亮2s,灭0.5s
    startTsunamiBlink(tsunamiWarningLayer)

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // 緊急地震速報
  async renderEEWReport(report, reportInfo, session) {
    setTitle(`${i18next.t("xml.report.name.eew")}\n${report.head?.headline_text || ""}`)
    if (report.head?.info_type === "取消") return this.renderCancelFallback(report)

    // 面板震度
    const buildShindoFromForecast = () => {
      const dict = {}
      const forecasts = report?.earth_quake?.intensity?.forecasts || []
      forecasts.forEach((f) => {
        let int
        if (f.int_form === f.int_to) int = f.int_to
        else if (f.int_to !== "over") int = f.int_to
        else int = f.int_from
        if (!dict[int]) dict[int] = []
        if (!dict[int].includes(f.area_name)) dict[int].push(f.area_name)
      })
      return dict
    }

    const shindoDict = buildShindoFromForecast()
    reportInfoWrite(shindoDict, reportInfo)

    // 要素
    const e = report.earth_quake?.epicenters?.[0]

    const { default: areaData } = await import("../map/jma_area.json")
    if (session !== this.renderSession) return

    const features = buildShindoFeaturesFromDict(shindoDict, areaData)
    if (e)
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [e.longitude, e.latitude] },
        properties: { intensity: "x" },
      })

    showBaseLayer(japanEEWMapLayer)

    const styleMap = { x: getEpicenterStyle(getAssetUrl, e?.name || "", 1) }
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = getIntensityStyle(getAssetUrl, k)

    const shindoLayer = createVectorLayerFromFeatures(features, styleMap, "shindo")

    // 预警区域(按都道府县)
    const f = new GeoJSON()
    let EEWAreaData = f.writeFeatures(japanEEWMapLayer.getSource().getFeatures(), {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    })
    EEWAreaData = JSON.parse(EEWAreaData)

    const forecasts = report?.earth_quake?.intensity?.forecasts || []
    const EEWWarningAreas = forecasts.filter((d) => d.category_name === "緊急地震速報（警報）")

    if (EEWWarningAreas.length > 0) {
      showEEWLegend()
      let warningLegendText = i18next.t("map.warning_areas")
      const prefSet = new Set()
      EEWWarningAreas.forEach((a) => prefSet.add(a.pref_name))
      for (const name of prefSet) warningLegendText += `\n${name}`
      setEEWLegend(i18next.t("map.warning_areas"))
      showInfoLegend()
      setInfoLegend(warningLegendText)
    }

    const areaLevelMap = {}
    EEWWarningAreas.forEach((e) => {
      const code = String(e.pref_code)
      areaLevelMap[code] = code
    })
    const codes = Object.keys(areaLevelMap)
    EEWAreaData.features = EEWAreaData.features.filter((feat) => codes.includes(feat.properties.code))

    const eewFillStyle = new Style({
      fill: new Fill({ color: "#faf500" }),
      stroke: new Stroke({ color: "#aaa", width: 1 }),
    })
    const EEWLayer = new VectorLayer({
      source: new VectorSource({
        features: new GeoJSON().readFeatures(EEWAreaData, { featureProjection: "EPSG:3857" }),
      }),
      style: () => eewFillStyle,
    })
    EEWLayer.set("name", "eew")

    applyReportLayers([EEWLayer, shindoLayer])

    const viewLayer = EEWWarningAreas.length === 0 ? shindoLayer : EEWLayer
    fitToLayer(viewLayer, 7)

    // 显示P波和S波位置(动态)
    if (e && e.longitude && e.latitude && e.depth && report.earth_quake?.origin_time && report.head.report_date_time) {
      try {
        const originTime = new Date(report.earth_quake.origin_time)
        const reportTime = new Date(report.head.report_date_time)

        ensureTimeLabel()
        startWaveAnimation(e, e.depth, originTime, reportTime)
      } catch (error) {
        console.error("Failed to show wave positions:", error)
      }
    }

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`

    // Dev Replay 按钮(在所有 innerHTML 操作完成后追加,避免被重建)
    document.getElementById("devModeBtn")?.remove()
    const newDevBtn = document.createElement("button")
    newDevBtn.id = "devModeBtn"
    newDevBtn.className = "btn btn-sm btn-outline-secondary mt-2 w-100"
    newDevBtn.textContent = "Dev Replay"
    newDevBtn.onclick = () => {
      if (!report.earth_quake?.origin_time || !report.head?.report_date_time) return

      try {
        const originTime = new Date(report.earth_quake.origin_time)
        const reportTime = new Date(report.head.report_date_time)
        startWaveAnimation(e, e.depth, originTime, reportTime, { devReplay: true })
      } catch (error) {
        console.error("Failed to start Dev Mode:", error)
      }
    }
    reportInfo.appendChild(newDevBtn)
  }
}
